import { useState, useEffect } from 'react'
import { X, Info, AlertTriangle, AlertCircle, CheckCircle } from 'lucide-react'
import { Button } from './button'

export function Banner({
  message,
  variant = 'info', // 'info', 'warning', 'error', 'success'
  dismissible = true,
  storageKey, // If provided, dismissal is persisted to localStorage
  action,
  onDismiss
}) {
  const [isVisible, setIsVisible] = useState(true)
  const [isAnimating, setIsAnimating] = useState(false)

  // Check localStorage on mount
  useEffect(() => {
    if (storageKey) {
      const dismissed = localStorage.getItem(`banner-dismissed-${storageKey}`)
      if (dismissed === 'true') {
        setIsVisible(false)
      }
    }
  }, [storageKey])

  // Slide in animation
  useEffect(() => {
    if (isVisible) {
      setTimeout(() => setIsAnimating(true), 10)
    }
  }, [isVisible])

  const handleDismiss = () => {
    setIsAnimating(false)
    setTimeout(() => {
      setIsVisible(false)
      if (storageKey) {
        localStorage.setItem(`banner-dismissed-${storageKey}`, 'true')
      }
      onDismiss?.()
    }, 300)
  }

  if (!isVisible) return null

  const icons = {
    info: <Info className="h-5 w-5" />,
    warning: <AlertTriangle className="h-5 w-5" />,
    error: <AlertCircle className="h-5 w-5" />,
    success: <CheckCircle className="h-5 w-5" />
  }

  const colors = {
    info: 'bg-info/10 border-info text-info',
    warning: 'bg-warning/10 border-warning text-warning',
    error: 'bg-destructive/10 border-destructive text-destructive',
    success: 'bg-success/10 border-success text-success'
  }

  return (
    <div
      className={`sticky top-0 z-40 border-b ${colors[variant]} transition-all duration-300 ${
        isAnimating ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            {icons[variant]}
            <p className="text-sm font-medium text-foreground">{message}</p>
          </div>

          <div className="flex items-center gap-3">
            {action && (
              <Button
                variant="outline"
                size="sm"
                onClick={action.onClick}
                className="h-8"
              >
                {action.label}
              </Button>
            )}
            
            {dismissible && (
              <button
                onClick={handleDismiss}
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Dismiss banner"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
